import {
    faAdd,
    faArrowLeft,
    faEllipsisVertical,
    faEnvelope,
    faTrash,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../AuthProvider';
import Button from '../../../components/Button';
import ButtonLink from '../../../components/ButtonLink';
import { BtnVariant } from '../../../components/ButtonVariant';
import { DropdownButton } from '../../../components/DropdownButton/DropdownButton';
import SideNav from '../../../components/SideNav';
import {
    contactIdPage,
    messagesIdPage,
    messagesPage,
} from '../../../constants/pagesRoutesContants';
import { classNames } from '../../../helpers/classNames';
import NavDataInterface from '../../../interfaces/NavDataInterface';
import OptionInterface from '../../../interfaces/OptionInterface';
import { fetchUserConversations } from '../../../services/messageService';
import ConversationType from '../../../types/ConversationType';
import UserType from '../../../types/UserType';
import ContactForm from './contactForm';
import Conversation from './conversation';
import styles from './style.module.scss';

function Message() {
    const { user } = useAuth();
    const { conversationId = '' } = useParams();
    const [messageText, setMessageText] = useState<string>('');
    const { data: conversations } = useQuery({
        queryKey: ['conversations', { search: messageText }],
        queryFn: async () => {
            try {
                const { data } = await fetchUserConversations(messageText);
                return data || [];
            } catch (error) {
                console.error(error);
            }
            return [];
        },
    });

    const [typeAction, setTypeAction] = useState<
        'newConversationForm' | 'conversation' | null
    >(null);

    const [recipient, setRecipient] = useState<OptionInterface | null>(null);
    const handleOnConversationClick = (contact: OptionInterface) => {
        if (contact) {
            setRecipient(contact);
            setTypeAction('conversation');
        }
    };

    useEffect(() => {
        if (conversationId) {
            setTypeAction('conversation');
        }
    }, [conversationId]);

    const navData: NavDataInterface[] = conversations?.map(
        (conversation: ConversationType): NavDataInterface => {
            return {
                id: conversation.id,
                label: conversation.name,
                link: messagesIdPage.path + conversation.id,
                variant: 'secondaryLight',
                icon: faEnvelope,
                iconColor: '#22b321',
            };
        }
    );

    const conversation = conversations?.find(
        (conversation: ConversationType) => conversation.id === conversationId
    );

    const recipientData = conversation?.users?.find(
        (msgUser: UserType) => msgUser.id !== user?.id
    );

    const handleClickNewConversation = () => {
        setTypeAction('newConversationForm');
    };

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setMessageText(value);
    };

    const viewObject = {
        conversation: {
            view: <Conversation recipient={recipient} />,
            toolbar: [
                {
                    label: 'Voir le contact',
                    icon: faUser,
                    variant: 'secondaryLight' as BtnVariant,
                    href: recipient?.id
                        ? contactIdPage.path + recipient?.id
                        : recipientData
                          ? contactIdPage.path + recipientData.id
                          : '',
                },
                {
                    label: 'Supprimer',
                    icon: faTrash,
                    variant: 'deleteLight' as BtnVariant,
                    onClick: () => {
                        setTypeAction('conversation');
                    },
                },
            ],
        },
        newConversationForm: {
            view: <ContactForm setRecipient={handleOnConversationClick} />,
            toolbar: [],
        },
    };

    return (
        <div className={styles.container}>
            <div
                className={classNames([
                    styles.sideNav,
                    typeAction ? styles.msgOpened : '',
                ])}
            >
                <div className={styles.sideNavHeader}>
                    <div className={styles.headerTitle}>Messages</div>
                    <Button
                        variant="secondaryLight"
                        className={styles.newNote}
                        onClick={handleClickNewConversation}
                    >
                        <FontAwesomeIcon icon={faAdd} /> Nouveau Message
                    </Button>
                </div>
                <div className={styles.sideNavContent}>
                    <SideNav
                        selected=""
                        navData={navData}
                        hasSearchBar={true}
                        searchBarPlaceholder="Nom, prénom..."
                        handleSearchChange={handleSearchChange}
                    />
                </div>
            </div>
            {!!typeAction && (
                <div className={styles.body}>
                    <div className={styles.bodyHeader}>
                        <ButtonLink
                            variant="secondaryLight"
                            onClick={() => setTypeAction(null)}
                            link={messagesPage.path}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </ButtonLink>
                        <div className={styles.bodyTitleContainer}>
                            <div className={styles.bodyTitle}>
                                {conversation?.name}
                            </div>
                            <div className={styles.bodySubTitle}>
                                {conversation?.lastEditDate
                                    ? new Date(
                                          conversation?.lastEditDate
                                      ).toLocaleDateString()
                                    : ''}
                            </div>
                        </div>
                        <div className={styles.bodyActionContainer}>
                            {viewObject[typeAction].toolbar.length > 0 && (
                                <DropdownButton
                                    items={viewObject[typeAction].toolbar}
                                    variant={'secondaryLight'}
                                    align="right"
                                >
                                    <FontAwesomeIcon
                                        icon={faEllipsisVertical}
                                    />
                                </DropdownButton>
                            )}
                        </div>
                    </div>
                    <div
                        className={classNames([
                            styles.bodyContent,
                            typeAction ? styles.msgOpened : '',
                        ])}
                    >
                        {typeAction && viewObject[typeAction].view}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Message;
